function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

class SiteNav extends HTMLElement {
  constructor() {
    super();

    this.siteHeader = this.closest(".header");
    this.isSticky = false;
    this.lastPosition = window.pageYOffset;

    this.qmbHeight = document.querySelector(".qmb").getBoundingClientRect().height;
  }

  connectedCallback() {
    this.scrollDetector();
  }

  scrollDetector() {
    window.addEventListener("scroll", () => {
      this.stickySituation();
    });
  }

  calculateCurrentOffset() {
    const pageOffset = window.scrollY;
    document.body.style.setProperty("--scrolltop-value", pageOffset + "px");

    return pageOffset;
  }

  stickySituation() {
    const pageOffset = this.calculateCurrentOffset();

    if (!this.classList.contains("is-visible") && pageOffset <= this.qmbHeight + this.getBoundingClientRect().height + 32) {
      this.classList.remove("is-hidden");
      this.classList.remove("is-visible");
      return;
    } else if (this.classList.contains("is-visible") && pageOffset <= this.qmbHeight) {
      this.classList.remove("is-hidden");
      this.classList.remove("is-visible");
      return;
    }

    if (this.lastPosition - pageOffset > 0) {
      this.classList.remove("is-hidden");
      this.classList.add("is-visible");
    } else {
      this.classList.add("is-hidden");
      this.classList.remove("is-visible");
    }

    this.lastPosition = pageOffset;
  }
}
if (!customElements.get("site-nav")) customElements.define("site-nav", SiteNav);

class MobileMenu extends HTMLElement {
  constructor() {
    super();

    this.closeEl = this.querySelector(".mobile-menu__close");
    this.overlay = this.querySelector(".mobile-menu__overlay");
    this.submenuTriggers = this.querySelectorAll(".mobile-menu__item--has-submenu");
    this.submenuClosers = this.querySelectorAll(".mobile-menu__submenu-close");
  }

  connectedCallback() {
    window.addEventListener("mobile-menu::open", e => this.open());

    this.closeEl.addEventListener("click", () => this.close());
    this.overlay.addEventListener("click", () => this.close());

    this.submenuTriggers.forEach(trigger => {
      trigger.addEventListener("click", e => {
        if (e.target != trigger) return;
        e.preventDefault();
        this.openSubmenu(trigger);
      });
    });
    this.submenuClosers.forEach(closer => {
      closer.addEventListener("click", e => {
        e.preventDefault();
        e.stopPropagation();
        this.closeSubmenu(closer.closest(".mobile-menu__submenu"));
      });
    });
  }

  open() {
    this.toggleAttribute("opened", true);
  }

  close() {
    this.toggleAttribute("opened", false);
  }

  openSubmenu(trigger) {
    const submenu = trigger.querySelector(".mobile-menu__submenu");
    submenu?.classList.add("is-visible");
  }

  closeSubmenu(submenu) {
    submenu.classList.remove("is-visible");
  }
}
if (!customElements.get("mobile-menu")) customElements.define("mobile-menu", MobileMenu);
class MobileMenuToggle extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("click", e => {
      window.dispatchEvent(new CustomEvent("mobile-menu::open"));
    });
  }
}
if (!customElements.get("mobile-menu-toggle")) customElements.define("mobile-menu-toggle", MobileMenuToggle);

class MegaMenu extends HTMLElement {
  constructor() {
    super();

    this.triggers = this.querySelectorAll(".sublink");
    this.contents = this.querySelectorAll(".mega-menu__contents [data-contents-of]");
    this.cards = this.querySelectorAll(".mega-menu__cards [data-contents-of]");
  }

  connectedCallback() {
    this.handle3rdLevels();
  }

  handle3rdLevels() {
    this.triggers.forEach(trigger => {
      trigger.addEventListener("click", e => {
        e.preventDefault();
        const contentTarget = Array.from(this.contents).filter(el => el.dataset.contentsOf == trigger.dataset.contents)[0];
        const cardTarget = Array.from(this.cards).filter(el => el.dataset.contentsOf == trigger.dataset.contents)[0];

        this.contents.forEach(el => el.classList.add("mega-menu__hidden"));
        this.cards.forEach(el => el.classList.add("mega-menu__hidden"));
        contentTarget?.classList.remove("mega-menu__hidden");
        cardTarget?.classList.remove("mega-menu__hidden");

        this.triggers.forEach(el => el.classList.remove("active"));
        trigger.classList.add("active");
      });
    });
  }
}
if (!customElements.get("mega-menu")) customElements.define("mega-menu", MegaMenu);

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.closeEl = this.querySelector(".cart__close");
    this.overlay = this.querySelector(".cart-drawer__overlay");
  }

  connectedCallback() {
    this.initialState();
    this.checkGiftEligibility();

    window.addEventListener("cart::open", e => this.open());
    window.addEventListener("cart::update", e => this.update(e.detail.cartContents));

    this.closeEl.addEventListener("click", () => this.close());
    this.overlay.addEventListener("click", () => this.close());
    this.querySelector(".btn--clear")?.addEventListener("click", () => this.clear());
  }

  open() {
    this.toggleAttribute("opened", true);
    this.closeEl.focus();
    // // Delay focus to ensure the drawer is visible first
    // setTimeout(() => {
    //   // Make sure the close button is focusable
    //   if (!this.closeEl.hasAttribute("tabindex")) {
    //     this.closeEl.setAttribute("tabindex", "0");
    //   }

    //   // Focus with a try-catch to help with debugging
    //   try {
    //     this.closeEl.focus();
    //     console.log("Focus set to close button:", document.activeElement === this.closeEl);
    //   } catch (e) {
    //     console.error("Error focusing close button:", e);
    //   }
    // }, 100);
  }

  close() {
    this.toggleAttribute("opened", false);
  }

  setLoading(state) {
    this.toggleAttribute("loading", state);
  }

  update(cartMarkup) {
    if (!cartMarkup) return;

    const cartHTML = this.parseHTML(cartMarkup);

    const cartContents = cartHTML.querySelector(".cart__contents");
    this.querySelector(".cart__contents").replaceWith(cartContents);

    const cartCountIndicator = cartHTML.querySelector(".cart__header span");
    const cartItemCount = cartHTML.querySelector("cart-drawer")?.dataset.itemCount;
    this.querySelector(".cart__header span").replaceWith(cartCountIndicator);

    const cartProgress = cartHTML.querySelector(".cart__progress");
    const cartProgressPercentageOld = this.dataset.progress;
    const cartProgressPercentage = cartHTML.querySelector("cart-drawer")?.dataset.progress;
    this.querySelector(".cart__progress").replaceWith(cartProgress);
    this.style.setProperty("--progress-from", cartProgressPercentageOld + "%");
    this.style.setProperty("--progress-current", cartProgressPercentage + "%");
    this.dataset.progress = cartProgressPercentage;

    this.dataset.thresholdGiftId = cartHTML.querySelector("cart-drawer")?.dataset.thresholdGiftId;
    this.dataset.thresholdGiftEligible = cartHTML.querySelector("cart-drawer")?.dataset.thresholdGiftEligible;
    this.dataset.thresholdGiftLineKey = cartHTML.querySelector("cart-drawer")?.dataset.thresholdGiftLineKey;
    this.checkGiftEligibility();

    window.dispatchEvent(
      new CustomEvent("cart::count", {
        detail: {
          count: parseInt(cartItemCount || 0, 10)
        }
      })
    );

    this.open();
  }

  parseHTML(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    return doc;
  }

  clear() {
    fetch(window.Shopify.routes.root + `cart/clear.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ sections: ["cart-contents"] })
    })
      .then(response => {
        if (!response.ok) {
          throw new Error("Something went wrong.");
        }
        return response.json();
      })
      .then(data => {
        this.update(data.sections["cart-contents"]);
      })
      .catch(error => {
        console.error("Error clearing cart:", error);
      });
  }

  checkGiftEligibility() {
    if (!this.dataset.thresholdGiftId || !this.dataset.thresholdGiftEligible) return;
    if (this.dataset.thresholdGiftEligible === "true" && (this.dataset.thresholdGiftLineKey === "" || this.dataset.thresholdGiftLineKey === undefined)) {
      this.addGiftToCart();
    } else if (this.dataset.thresholdGiftEligible !== "true" && this.dataset.thresholdGiftLineKey !== "" && this.dataset.thresholdGiftLineKey !== undefined) {
      this.removeGiftFromCart();
    }
  }

  addGiftToCart() {
    const body = JSON.stringify({
      items: [
        {
          id: this.dataset.thresholdGiftId,
          quantity: 1
        }
      ],
      sections: "cart-contents"
    });

    fetch(window.Shopify.routes.root + "cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: body
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(data => {
            throw new Error(data.description || `Error: ${response.status}`);
          });
        }
        return response.json();
      })
      .then(data => {
        window.dispatchEvent(
          new CustomEvent("cart::update", {
            detail: {
              cartContents: data.sections["cart-contents"]
            }
          })
        );
      })
      .catch(error => {
        console.error("Error adding to cart:", error);
        this.showError(error.message || "Could not add to cart. Please try again.");
      })
      .finally(() => {
        this.setLoading(false);
      });
  }
  removeGiftFromCart() {
    const body = JSON.stringify({
      updates: {
        [this.dataset.thresholdGiftLineKey]: 0
      },
      sections: "cart-contents"
    });

    fetch(window.Shopify.routes.root + "cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: body
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(data => {
            throw new Error(data.description || `Error: ${response.status}`);
          });
        }
        return response.json();
      })
      .then(data => {
        window.dispatchEvent(
          new CustomEvent("cart::update", {
            detail: {
              cartContents: data.sections["cart-contents"]
            }
          })
        );
      })
      .catch(error => {
        console.error("Error adding to cart:", error);
        this.showError(error.message || "Error occured.");
      })
      .finally(() => {
        this.setLoading(false);
      });
  }

  initialState() {
    const urlParams = new URLSearchParams(window.location.search);
    const action = urlParams.get("action");

    if (action == "show-cart") {
      urlParams.delete("action", "show-cart");
      const updatedUrl = window.location.pathname + urlParams.toString();
      window.history.replaceState(null, "", updatedUrl);

      this.open();
    }
  }
}
if (!customElements.get("cart-drawer")) customElements.define("cart-drawer", CartDrawer);
class CartCoupon extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector("form");
    this.input = this.querySelector("input");
    this.submit = this.querySelector("button[type='submit']");
    this.fieldError = this.querySelector(".field__error");
    this.appliedCouponsContainer = this.querySelector(".cart-coupon__applied");
    this.appliedCoupons = this.querySelectorAll(".cart-coupon__applied li button");
    this.appliedCouponsString = this.appliedCouponsContainer.dataset.coupons || "";
  }
  connectedCallback() {
    this.form.addEventListener("submit", e => {
      e.preventDefault();
      this.submitCoupon(`${this.appliedCouponsString ? this.appliedCouponsString + "," : ""}${this.input.value}`);
    });

    this.appliedCoupons.forEach(coupon => {
      coupon.addEventListener("click", e => {
        e.preventDefault();
        this.submitCoupon(coupon.dataset.couponsLeft || "");
      });
    });
  }

  setLoading(state) {
    this.submit.toggleAttribute("loading", state);
  }

  submitCoupon(couponsLeft) {
    this.setLoading(true);
    const body = JSON.stringify({
      discount: couponsLeft || "",
      sections: "cart-contents"
    });

    console.log("Submitting coupon:", body);

    fetch(window.Shopify.routes.root + `cart/update.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: body
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(data => {
            throw new Error(data.description || `Error: ${response.status}`);
          });
        }
        return response.json();
      })
      .then(data => {
        window.dispatchEvent(
          new CustomEvent("cart::update", {
            detail: {
              cartContents: data.sections["cart-contents"]
            }
          })
        );
      })
      .catch(error => {
        console.error("Error submitting coupon:", error);
        this.fieldError.textContent = error.message || "Could not remove coupon. Please try again.";
        setTimeout(() => {
          this.fieldError.textContent = "";
        }, 3000);
      })
      .finally(() => {
        this.setLoading(false);
        this.input.value = "";
      });
  }
}
if (!customElements.get("cart-coupon")) customElements.define("cart-coupon", CartCoupon);

class CartItem extends HTMLElement {
  constructor() {
    super();

    this.cart = this.closest("cart-drawer");
    this.qtyInput = this.querySelector(".qty input");
    this.quantity = this.qtyInput?.value;

    this.switchToSubscription = this.querySelector(".cart-item__switch-to-subscription");
    this.purchaseOptions = this.querySelector(".cart-item__footer-right select");
    this.removeButton = this.querySelector(".cart-item__remove");
    this.addUpsellItem = this.querySelector(".cart-item__add-upsell-item");

    this.errorContainer = this.querySelector(".error-container");
  }

  connectedCallback() {
    this.switchToSubscription?.addEventListener("click", e => {
      e.preventDefault();
      this.handleCartLineUpdate({
        selling_plan: e.target.dataset.sellingPlanId,
        quantity: this.quantity
      });
    });
    this.purchaseOptions?.addEventListener("change", e => {
      e.preventDefault();
      this.handleCartLineUpdate({
        selling_plan: this.purchaseOptions.value,
        quantity: this.quantity
      });
    });
    this.qtyInput?.addEventListener(
      "change",
      debounce(e => {
        e.preventDefault();
        this.handleCartLineUpdate({
          quantity: e.target.value
        });
      }, 300)
    );
    this.removeButton?.addEventListener("click", e => {
      e.preventDefault();
      this.handleCartLineUpdate({
        quantity: 0
      });
    });
    this.addUpsellItem?.addEventListener("click", e => {
      e.preventDefault();
      e.target.toggleAttribute("loading", true);
      this.handleCartLineUpdate(
        {
          id: e.target.dataset.variantId,
          quantity: 1
        },
        "add"
      );
    });
  }

  handleCartLineUpdate(changes, action = "change") {
    this.setCartLoading();

    const payload = {
      id: this.dataset.lineKey,
      sections: "cart-contents",
      ...changes
    };

    fetch(window.Shopify.routes.root + `cart/${action}.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(payload)
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(data => {
            throw new Error(data.description || `Error: ${response.status}`);
          });
        }
        return response.json();
      })
      .then(data => {
        window.dispatchEvent(
          new CustomEvent("cart::update", {
            detail: {
              cartContents: data.sections["cart-contents"]
            }
          })
        );
      })
      .catch(error => {
        console.error("Error updating cart:", error);
        this.qtyInput != undefined && (this.qtyInput.value = this.quantity);
        this.errorContainer.textContent = error.message || "Could not update cart. Please try again.";
        setTimeout(() => {
          this.errorContainer.textContent = "";
        }, 3000);
      })
      .finally(() => {
        this.cart.setLoading(false);
      });
  }

  setCartLoading() {
    this.cart.setLoading(true);
  }
}
if (!customElements.get("cart-item")) customElements.define("cart-item", CartItem);

class CartToggle extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector(".cart-toggle__button");
    this.indicator = this.querySelector(".cart-toggle__indicator");
  }

  connectedCallback() {
    this.addEventListener("click", () => {
      window.dispatchEvent(new CustomEvent("cart::open"));
    });
    window.addEventListener("cart::count", e => {
      this.updateIndicator(e.detail.count);
    });
  }

  updateIndicator(count) {
    this.indicator.dataset.count = count;
    this.indicator.textContent = count;
  }
}
if (!customElements.get("cart-toggle")) customElements.define("cart-toggle", CartToggle);

class AddToCart extends HTMLElement {
  constructor() {
    super();

    this.submit = this.querySelector('button[type="submit"]');
    this.form = this.form = this.querySelector("form") || this.closest("form");
    this.quantity = this.querySelector("quantity-input");
    this.errorContainer = this.querySelector(".error-container");
    this.priceElement = this.querySelector(".atc__price");
    this.defaultSellingPlan = this.querySelector("[data-default-selling-plan]");
    this.bisNotifyButton = this.querySelector("[klaviyo-bis-trigger]");
  }

  connectedCallback() {
    this.submit?.addEventListener("click", e => {
      e.preventDefault();
      this.setLoading(true);
      this.addToCart();
    });

    // Also handle form submit event for better accessibility
    this.form?.addEventListener("submit", e => {
      e.preventDefault();
      this.setLoading(true);
      this.addToCart();
    });

    this.bisNotifyButton?.addEventListener("click", e => {
      e.preventDefault();

      const formId = this.bisNotifyButton.dataset.formId;
      window._klOnsite ? window._klOnsite.openForm(formId) : console.error("Klaviyo Onsite is not initialized.");
    });

    if (this.priceElement) {
      window.addEventListener("add-to-cart::price-change", e => {
        const targetId = e.detail.targetId;
        if (!targetId || targetId === this.id) {
          this.priceElement.textContent = e.detail.newPrice;
        }
      });
    }

    if (this.defaultSellingPlan) {
      window.addEventListener("purchase-option::change", e => {
        this.defaultSellingPlan.value = "";
        if (e.detail.value === "subscription") {
          if (e.detail.id) {
            this.defaultSellingPlan.value = e.detail.id;
          }
        }
      });
    }
  }

  setLoading(state) {
    this.submit.toggleAttribute("loading", state);
  }

  addToCart() {
    const formData = new FormData(this.form);

    if (!formData.get("id")) {
      console.error("Product ID not found in form");
      this.showError("Could not identify product. Please try again.");
      this.setLoading(false);
      return;
    }

    const body = JSON.stringify({
      items: [
        {
          id: formData.get("id"),
          quantity: parseInt(formData.get("quantity") || 1, 10),
          selling_plan: formData.get("selling_plan") || null,
          ...this.getFormProperties(formData)
        }
      ],
      sections: "cart-contents"
    });

    fetch(window.Shopify.routes.root + "cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: body
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(data => {
            throw new Error(data.description || `Error: ${response.status}`);
          });
        }
        return response.json();
      })
      .then(data => {
        window.dispatchEvent(
          new CustomEvent("cart::update", {
            detail: {
              cartContents: data.sections["cart-contents"]
            }
          })
        );
      })
      .catch(error => {
        console.error("Error adding to cart:", error);
        this.showError(error.message || "Could not add to cart. Please try again.");
      })
      .finally(() => {
        this.setLoading(false);
      });
  }

  getFormProperties(formData) {
    const properties = {};
    let hasProperties = false;

    for (const [key, value] of formData.entries()) {
      if (key.startsWith("properties[") && key.endsWith("]")) {
        const propertyName = key.slice(11, -1); // Extract property name
        properties[propertyName] = value;
        hasProperties = true;
      }
    }

    return hasProperties ? { properties } : {};
  }

  showError(message) {
    if (this.errorContainer) {
      this.errorContainer.textContent = message;
      setTimeout(() => {
        this.errorContainer.textContent = "";
      }, 3000);
    }
  }
}
if (!customElements.get("add-to-cart")) customElements.define("add-to-cart", AddToCart);

class QuickBuy extends HTMLElement {
  constructor() {
    super();

    this.mainProductSection = document.querySelector(".product__buy-form .btn--atc");
    this.footer = document.querySelector(".shopify-section-group-footer-group");
    this.isVisible = false;
    this.threshold = 0;
    this.footerThreshold = 0;

    this.oneTimePurchaseSwitch = this.querySelector("[data-onetime-switch]");
    this.subscriptionSwitch = this.querySelector("[data-subscription-switch]");

    this.mobilePurchaseOptionSwitch = this.querySelector(".quick-buy__description-mobile select");
    this.mobilePrice = this.querySelector(".quick-buy__description-mobile .t-price");

    this.defaultSellingPlan = this.querySelector("[data-default-selling-plan]");
  }

  connectedCallback() {
    if (!this.mainProductSection) return;

    window.addEventListener("load", () => {
      this.calculateThresholds();
    });

    window.addEventListener(
      "resize",
      debounce(() => {
        this.calculateThresholds();
      }, 200)
    );

    window.addEventListener("scroll", () => {
      this.handleScroll();
    });

    this.subscriptionSwitch?.addEventListener("click", e => {
      window.dispatchEvent(
        new CustomEvent("purchase-option::change", {
          detail: {
            value: "subscription",
            id: e.target.value
          }
        })
      );
      this.scrollToMainForm();
    });

    this.oneTimePurchaseSwitch?.addEventListener("click", e => {
      window.dispatchEvent(
        new CustomEvent("purchase-option::change", {
          detail: {
            value: "one-time"
          }
        })
      );
    });

    this.mobilePurchaseOptionSwitch?.addEventListener("change", e => {
      window.dispatchEvent(
        new CustomEvent("purchase-option::change", {
          detail: {
            value: e.target.value
          }
        })
      );
      this.scrollToMainForm();
    });

    window.addEventListener("purchase-option::change", e => {
      if (e.detail.value === "subscription") {
        this.subscriptionSwitch && (this.subscriptionSwitch.checked = true);
        this.mobilePurchaseOptionSwitch && (this.mobilePurchaseOptionSwitch.value = "subscription");
      } else {
        this.oneTimePurchaseSwitch && (this.oneTimePurchaseSwitch.checked = true);
        this.mobilePurchaseOptionSwitch && (this.mobilePurchaseOptionSwitch.value = "one-time");
      }
    });

    window.addEventListener("add-to-cart::price-change", e => {
      if (this.mobilePrice != undefined) this.mobilePrice.textContent = e.detail.newPrice;
    });
  }

  calculateThresholds() {
    if (!this.mainProductSection) return;

    const rect = this.mainProductSection.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    this.threshold = rect.bottom + scrollTop;

    if (this.footer) {
      const footerRect = this.footer.getBoundingClientRect();
      this.footerThreshold = footerRect.top + scrollTop - window.innerHeight;
    }
  }

  scrollToMainForm() {
    if (this.defaultSellingPlan) return;

    const formSection = this.mainProductSection.closest(".product__buy-form") || this.mainProductSection;

    if (!formSection) return;

    const formRect = formSection.getBoundingClientRect();
    const offsetPosition = window.pageYOffset + formRect.top - 128;

    const currentPosition = window.pageYOffset;
    const distance = Math.abs(offsetPosition - currentPosition);
    const estimatedTime = Math.min(1000, Math.max(500, distance * 0.5));

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth"
    });

    setTimeout(() => {
      formSection.classList.add("highlight-subscriptions");
      setTimeout(() => {
        formSection.classList.remove("highlight-subscriptions");
      }, 300);
    }, estimatedTime);
  }

  handleScroll() {
    if (!this.mainProductSection) return;

    const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

    const shouldShow = scrollPosition > this.threshold && (!this.footer || scrollPosition < this.footerThreshold);

    if (shouldShow && !this.isVisible) {
      this.classList.add("is-visible");
      this.isVisible = true;
    } else if (!shouldShow && this.isVisible) {
      this.classList.remove("is-visible");
      this.isVisible = false;
    }
  }
}
if (!customElements.get("quick-buy")) customElements.define("quick-buy", QuickBuy);

class PurchaseOptions extends HTMLElement {
  constructor() {
    super();
    this.optionLabels = this.querySelectorAll(".purchase-options__plan-grid label.purchase-options__plan-name");
    this.individualOptions = this.querySelectorAll('[data-radio-type="selling_plan"]');

    this.oneTimePurchaseSwitch = this.querySelector("[data-onetime-switch]");
    this.subscriptionSwitch = this.querySelector("[data-subscription-switch]");
  }

  connectedCallback() {
    this.optionLabels?.forEach(label => {
      label.addEventListener("click", () => {
        const planOptions = label.closest(".purchase-options__plan-grid").querySelectorAll('.purchase-options__plan-options input[type="radio"]');
        if (planOptions.length == 0) return;

        const hasAnyChecked = Array.from(planOptions).some(option => option.checked);
        if (hasAnyChecked) return;

        planOptions[0].checked = true;
        this.handleOptionChange(planOptions[0]);
      });
    });

    this.oneTimePurchaseSwitch?.addEventListener("click", e => {
      e.stopPropagation();
      window.dispatchEvent(
        new CustomEvent("purchase-option::change", {
          detail: {
            value: "one-time"
          }
        })
      );
    });

    this.subscriptionSwitch?.addEventListener("click", e => {
      window.dispatchEvent(
        new CustomEvent("purchase-option::change", {
          detail: {
            value: "subscription"
          }
        })
      );
    });

    this.individualOptions?.forEach(option => {
      option.addEventListener("change", e => {
        this.handleOptionChange(option);
        window.dispatchEvent(
          new CustomEvent("purchase-option::change", {
            detail: {
              value: "subscription"
            }
          })
        );
      });
    });

    window.addEventListener("purchase-option::change", e => {
      if (e.detail.value === "subscription") {
        const plan = this.subscriptionSwitch.closest(".purchase-options__plan-grid");
        const planOptions = plan.querySelectorAll('.purchase-options__plan-options input[type="radio"]');
        if (planOptions.length == 0) return;

        const hasAnyChecked = Array.from(planOptions).some(option => option.checked);
        if (hasAnyChecked) return;

        planOptions[0].checked = true;
        this.handleOptionChange(planOptions[0]);
      } else {
        const oneTimePurchaseOption = this.oneTimePurchaseSwitch.querySelector('input[type="radio"]');
        if (!oneTimePurchaseOption) return;
        oneTimePurchaseOption.checked = true;
        this.handleOptionChange(oneTimePurchaseOption);
      }
    });
  }

  handleOptionChange(option) {
    const plan = option.closest(".purchase-options__plan-grid");
    const planDiscount = plan?.querySelector(".purchase-options__plan-discount span");
    const planPrice = plan?.querySelector(".purchase-options__plan-price");

    const optionDiscountType = option.dataset.sellingPlanAdjustmentType;
    const optionDiscountValue = option.dataset.sellingPlanAdjustmentValue;
    const planUpdatedDiscount = optionDiscountType === "percentage" ? `${optionDiscountValue}%` : `$${optionDiscountValue}`;
    const optionUpdatedPrice = option.dataset.variantPrice;

    if (planDiscount) planDiscount.textContent = planUpdatedDiscount;
    if (planPrice) planPrice.textContent = optionUpdatedPrice;

    const addToCartElement = this.closest("form")?.querySelector("add-to-cart") || document.querySelector(`add-to-cart[id="${this.dataset.targetCart}"]`);
    const targetId = addToCartElement?.id || null;
    window.dispatchEvent(
      new CustomEvent("add-to-cart::price-change", {
        detail: {
          newPrice: optionUpdatedPrice,
          targetId: targetId
        }
      })
    );
  }
}
if (!customElements.get("purchase-options")) customElements.define("purchase-options", PurchaseOptions);

class SearchToggle extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("click", event => {
      event.stopPropagation();
      window.dispatchEvent(new Event("search::open"));
    });
  }
}
if (!customElements.get("search-toggle")) customElements.define("search-toggle", SearchToggle);

class MainMenu extends HTMLElement {
  constructor() {
    super();

    this.overlay = document.querySelector(".site-overlay");
    this.megaMenuLinks = Array.from(this.querySelector("ul").children);
    this.megaMenuTriggers = this.querySelectorAll("li.has-mega-menu");
    this.megaMenuCurrent = null;
  }

  connectedCallback() {
    this.megaMenuTriggers.forEach(trigger => {
      trigger.addEventListener("click", e => {
        if (e.target !== trigger) return;
        e.preventDefault();

        if (this.megaMenuCurrent == null) this.open(trigger);
        else this.close();
      });
      trigger.addEventListener("mouseenter", e => {
        if (e.target !== trigger) return;
        e.preventDefault();

        if (this.megaMenuCurrent == null) this.open(trigger);
      });
      trigger.addEventListener("keydown", e => {
        if (e.key !== "Enter" && e.key !== " ") return;
        if (!Array.from(this.megaMenuTriggers).includes(trigger)) return;
        e.preventDefault();
        this.megaMenuCurrent == null ? this.open(trigger) : this.close();
      });

      trigger.addEventListener("focusout", e => {
        // Only close if the new focus target is outside the current mega menu
        if (this.megaMenuCurrent != null) {
          // Give a small delay to check where the focus has moved
          setTimeout(() => {
            const focusedElement = document.activeElement;
            // Check if the focused element is within the current mega menu
            if (!this.megaMenuCurrent.contains(focusedElement) && !trigger.contains(focusedElement)) {
              this.close();
            }
          }, 10);
        }
      });
    });

    document.body.addEventListener("keydown", e => {
      if (e.key === "Escape" && this.megaMenuCurrent != null) {
        this.close();
      }
    });

    document.addEventListener("scroll", e => {
      if (this.megaMenuCurrent != null) this.close();
    });

    this.overlay.addEventListener("click", () => {
      if (this.megaMenuCurrent != null) this.close();
    });
  }

  open(trigger) {
    this.overlay.classList.add("is-visible");
    trigger.querySelector("mega-menu").classList.add("is-visible");
    this.megaMenuCurrent = trigger.querySelector("mega-menu");
  }

  close() {
    this.overlay.classList.remove("is-visible");
    this.megaMenuCurrent.classList.remove("is-visible");
    this.megaMenuCurrent = null;
  }
}
if (!customElements.get("main-menu")) customElements.define("main-menu", MainMenu);

class HotSpot extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.handleFadeIn();
  }

  handleFadeIn() {
    this.querySelector("details").addEventListener("toggle", e => {
      const hotspotDetails = e.target.querySelector(".hotspot-details");
      if (e.newState == "open") {
        hotspotDetails.classList.add("animatingIn");
        setTimeout(() => {
          e.target.querySelector(".hotspot-details").classList.add("animated");
        }, 150);
      } else hotspotDetails.classList.remove("animatingIn", "animated");
    });
  }
}
if (!customElements.get("hot-spot")) customElements.define("hot-spot", HotSpot);

class QuantityInput extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector("input");
    this.plus = this.querySelector(".qty__increase");
    this.minus = this.querySelector(".qty__decrease");
    this.productQtyInput = this.closest(".qty-input");
    this.cartQtyInput = this.closest(".qty-input");
  }

  connectedCallback() {
    this.plus.addEventListener("click", () => this.increment());
    this.minus.addEventListener("click", () => this.decrement());

    this.controlValue();
  }

  increment() {
    this.input.value = parseInt(this.input.value) + 1;
    this.input.dispatchEvent(new Event("change", { bubbles: true }));
    this.controlValue();
  }

  decrement() {
    if (this.input.value > 1) {
      this.input.value = parseInt(this.input.value) - 1;
      this.input.dispatchEvent(new Event("change", { bubbles: true }));
    }
    this.controlValue();
  }

  controlValue() {
    if (this.input.value == 1) this.minus.toggleAttribute("disabled", true);
    else this.minus.toggleAttribute("disabled", false);
  }
}
if (!customElements.get("quantity-input")) customElements.define("quantity-input", QuantityInput);

class NewsletterForm extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector("form");
    this.submitButton = this.querySelector('button[type="submit"]');
    this.inputs = this.querySelectorAll("input");
    this.statusMessage = this.querySelector(".newsletter-form__status") || this.createElement("div", "newsletter-form__status");
  }

  connectedCallback() {
    this.inputs?.forEach(input => {
      input.addEventListener("input", e => {
        if (input.type === "email") {
          this.validate(input);
        }
        this.validate(input);
      });
    });

    this.submitButton?.addEventListener("click", e => {
      e.preventDefault();
      this.submit();
    });

    this.form?.addEventListener("submit", e => {
      e.preventDefault();
      this.submit();
    });
  }

  createElement(tag, className) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    this.appendChild(element);
    return element;
  }

  setLoading(state) {
    this.submitButton.toggleAttribute("loading", state);
  }

  validate(input) {
    const fieldContainer = input.closest(".field");
    let isFieldValid = input.checkValidity();
    if (input.type === "email") input.value.match(input.pattern) ? (isFieldValid = true) : (isFieldValid = false);
    if (!isFieldValid) {
      fieldContainer?.classList.add("invalid");
    } else {
      fieldContainer?.classList.remove("invalid");
    }
  }

  submit() {
    this.inputs.forEach(input => {
      this.validate(input);
    });

    if (this.form.checkValidity()) {
      this.setLoading(true);
      this.send();
    }
  }

  send() {
    // Implement newsletter submission
    const formData = new FormData(this.form);
    const data = new URLSearchParams(formData);

    fetch(this.form.action, {
      method: "POST",
      body: data,
      headers: {
        "Content-Type": "application/x-www-form-urlencoded"
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then(data => {
        this.handleSuccess();
      })
      .catch(error => {
        this.handleError(error);
      })
      .finally(() => {
        this.setLoading(false);
      });
  }

  handleSuccess() {
    if (this.statusMessage) {
      this.statusMessage.textContent = "Thank you for subscribing!";
      this.statusMessage.classList.add("success");
      this.statusMessage.classList.remove("error");
    }
    this.form.reset();
  }

  handleError(error) {
    if (this.statusMessage) {
      this.statusMessage.textContent = "Subscription failed. Please try again.";
      this.statusMessage.classList.add("error");
      this.statusMessage.classList.remove("success");
    }
  }
}
if (!customElements.get("newsletter-form")) customElements.define("newsletter-form", NewsletterForm);

class MetaobjectFAQ extends HTMLElement {
  constructor() {
    super();

    this.triggers = this.querySelectorAll(".faq-categories__trigger");
    this.contents = this.querySelectorAll(".faq-categories__wrapper");
  }

  connectedCallback() {
    this.triggers.forEach((trigger, index) => {
      trigger.addEventListener("click", () => this.toggle(index));
    });
  }

  toggle(index) {
    this.contents.forEach((content, i) => {
      const isActive = content.style.display === "block";
      content.style.display = i === index && !isActive ? "block" : "none";
      this.triggers[i].classList.toggle("is-active", i === index && !isActive);
    });
  }

  // toggleAnswers(index) {
  //   if (this.categoryWrappers[index].style.display === "block") return;

  //   this.categoryTitles.forEach(title => {
  //     title.classList.remove("is-active");
  //   });

  //   this.categoryTitles[index].classList.add("is-active");

  //   this.categoryWrappers.forEach(wrapper => {
  //     wrapper.style.display = "none";
  //   });

  //   this.categoryWrappers[index].style.display = "block";

  //   const activeWrapperHeight = this.categoryWrappers[index].offsetHeight;
  //   this.style.height = `${activeWrapperHeight + 200}px`;
  // }
}
if (!customElements.get("faq-categories")) customElements.define("faq-categories", MetaobjectFAQ);

class OverlayToggle extends HTMLElement {
  constructor() {
    super();

    this.productCard = this.closest(".product-card");
    this.button = this.querySelector("button");
  }

  connectedCallback() {
    this.button.addEventListener("click", e => {
      e.preventDefault();
      this.toggleOverlay();
    });
  }

  toggleOverlay() {
    this.productCard.classList.toggle("overlay-visible");
  }
}
if (!customElements.get("overlay-toggle")) customElements.define("overlay-toggle", OverlayToggle);

// Utils
window.addEventListener("DOMContentLoaded", () => {
  if (typeof AOS)
    AOS.init({
      duration: 400,
      once: true,
      disable: "mobile"
    });

  const usingMouse = () => {
    document.body.classList.add("using-mouse");
    document.body.classList.remove("using-keyboard");
  };
  const usingTab = evt => {
    if (evt.key === "Tab") {
      document.body.classList.remove("using-mouse");
      document.body.classList.add("using-keyboard");
    }
  };
  document.body.addEventListener("mousedown", usingMouse);
  document.body.addEventListener("keydown", usingTab);
});

window.addEventListener("shopify:section:load", () => {
  if (typeof AOS)
    AOS.init({
      duration: 400,
      once: true,
      disable: "mobile"
    });
});
